import { useEffect, useState, useRef } from "react";
import SocialMediaEmbed from "./SocialMediaEmbed";

const POSTS_PER_LOAD = 10;

const SocialMediaFeed = ({ posts }) => {
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_LOAD);
  const [globalSoundEnabled, setGlobalSoundEnabled] = useState(false); // Default to muted (false)
  const videoRefs = useRef({});

  // Load more posts on scroll
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const fullHeight = document.documentElement.scrollHeight;

    if (scrollTop + windowHeight >= fullHeight - 100) {
      setVisibleCount((prev) => Math.min(prev + POSTS_PER_LOAD, posts.length));
    }
  };

  const handleManualMute = () => {
    setGlobalSoundEnabled(false); // Mute all posts
    Object.values(videoRefs.current).forEach((v) => {
      if (v) v.muted = true;
    });
  };

  const handleManualUnmute = () => {
    setGlobalSoundEnabled(true); // Unmute all posts
    Object.values(videoRefs.current).forEach((v) => {
      if (v) v.muted = false;
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [posts.length]);

  // Handle autoplay/pause based on visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!video) return;

          if (entry.isIntersecting) {
            video.play().catch(() => {}); // Play if in view
          } else {
            video.pause(); // Pause when out of view
          }
        });
      },
      { threshold: 0.5 },
    );

    Object.values(videoRefs.current).forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => {
      Object.values(videoRefs.current).forEach((video) => {
        if (video) observer.unobserve(video);
      });
    };
  }, [posts, visibleCount]);

  return (
    <div className="mx-auto flex w-[400px] flex-col space-y-8">
      {posts.slice(0, visibleCount).map((post, index) => (
        <SocialMediaEmbed
          key={post.id || index}
          platform={post.platform}
          url={post.url || post.src}
          type={post.type}
          caption={post.post_caption}
          creatorName={post.post_creator_name}
          downloaded_path={post.downloaded_path}
          videoRef={(el) => {
            if (el) {
              videoRefs.current[index] = el;
            } else {
              delete videoRefs.current[index];
            }
          }}
          muted={!globalSoundEnabled} // Muted by default
          onManualUnmute={handleManualUnmute}
          onManualMute={handleManualMute}
        />
      ))}
    </div>
  );
};

export default SocialMediaFeed;
